import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { TableCell, TableRow } from '@material-ui/core';
import { paymentMode } from '@ourbranch/lookups';

import { Loading, Table } from 'core';
import { Button } from 'core/components/button';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import Section from 'core/components/section';
import { Tag } from 'core/components/tag';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { apolloClient } from 'core/apollo-client';
import { awsDateToDateFormatter, currencyFormatter } from 'core/helpers/formatters';
import { isGuestPaymentLinkAllowed } from 'core/helpers/guest-payment-links';
import { isSendInvoiceAllowed } from 'core/helpers/send-invoice';
import { getBillingPaymentMethod } from '../policy/payment-tab/billing-details/billing-details';
import { getOpenPolicies } from '../workspace/workspace-summary';
import {
  BillingLoad,
  getBillingHistory,
  getBillingSummary,
  getPaymentMethods,
  getPlanLabel,
  getPolicyLine,
  getPriceRows,
  getScheduleRows,
  joinPolicyLines
} from './billing-summary';
import { GET_POLICY_BILLING_DETAILS } from './household-billing.queries';
import useStyles from './household-billing.styles';

const formatDate = (date) => (date ? awsDateToDateFormatter(date) : '—');

const paymentTabLink = (accountId, policyId) => `/customer/${accountId}/policy/${policyId}?tab=payment`;

const methodLabel = (method) => getBillingPaymentMethod(null, method.brand, method.last4);

// The billing forms are tied to the policy page's one-policy store, so every action opens that
// policy's Payment & Price tab.
const getPolicyActions = ({ session, policy }) =>
  [
    !session.viewOnly && { label: 'Take a payment' },
    session.canEditPolicies && isGuestPaymentLinkAllowed() && { label: 'Send payment link' },
    session.canEditPolicies && isSendInvoiceAllowed() && { label: 'Send invoice' },
    session.canEditPolicies && { label: 'Change plan or billing day' },
    session.isInternalAgent &&
      !session.viewOnly &&
      policy.paymentMode === paymentMode.Automated && { label: 'Pause autopay', internal: true }
  ].filter(Boolean);

const InternalTag = () => <Tag variant="internal">Internal</Tag>;

const getAutopayLabel = (policies) => {
  const on = policies.filter((policy) => policy.paymentMode === paymentMode.Automated);
  if (on.length === policies.length) {
    return 'On';
  }
  if (!on.length) {
    return 'Off';
  }
  const off = policies.filter((policy) => !on.includes(policy));
  return `On for ${joinPolicyLines(on)}, off for ${joinPolicyLines(off)}`;
};

const Fact = ({ label, children }) => {
  const classes = useStyles();
  return (
    <div className={classes.fact}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
};

Fact.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

const BillingSummary = ({ policies, summary }) => {
  const classes = useStyles();
  const { complete, failedPolicies, pastDue, nextPayment, defaultMethods } = summary;

  let nextPaymentValue;
  if (!complete) {
    nextPaymentValue = failedPolicies.length ? 'Not shown until every policy loads' : 'Loading…';
  } else if (pastDue.length) {
    nextPaymentValue = pastDue
      .map(
        ({ policy, billing }) =>
          `${currencyFormatter(billing.nextPaymentTotal ?? billing.nextPaymentAmount)} · ${getPolicyLine(policy)}`
      )
      .join(', ');
  } else if (nextPayment) {
    nextPaymentValue = `${currencyFormatter(nextPayment.amount)} on ${formatDate(nextPayment.date)} · ${joinPolicyLines(nextPayment.policies)}`;
  } else {
    nextPaymentValue = 'Nothing scheduled';
  }

  let defaultMethodValue = complete ? '—' : 'Loading…';
  // Until every policy loads, name the policies a method covers so it doesn't read as the household's.
  if (complete && defaultMethods.length === 1) {
    defaultMethodValue = methodLabel(defaultMethods[0].method);
  } else if (defaultMethods.length) {
    defaultMethodValue = defaultMethods.map(({ method, policies: usedBy }) => (
      <span key={method.id ?? method.last4} className={classes.methodLine}>
        {joinPolicyLines(usedBy)}: {methodLabel(method)}
      </span>
    ));
  }

  return (
    <Section title="Billing summary" type="SubSection">
      <Card type="primary" className={classes.card}>
        <dl className={classes.facts}>
          <Fact label={pastDue.length ? 'Past due' : 'Next payment'}>
            <span className={pastDue.length ? classes.pastDue : undefined}>{nextPaymentValue}</span>
          </Fact>
          <Fact label="Autopay">{getAutopayLabel(policies)}</Fact>
          <Fact label="Default payment method">{defaultMethodValue}</Fact>
          <Fact label="Policies billed">{joinPolicyLines(policies)}</Fact>
        </dl>
        {failedPolicies.length > 0 && (
          <p role="status" className={classes.summaryWarning}>
            {joinPolicyLines(failedPolicies)} billing didn't load, so this summary leaves out the next payment.
          </p>
        )}
      </Card>
    </Section>
  );
};

BillingSummary.propTypes = {
  policies: PropTypes.array.isRequired,
  summary: PropTypes.object.isRequired
};

const ScheduleCard = ({ accountId, policy, result, onRetry, actions }) => {
  const classes = useStyles();
  const line = getPolicyLine(policy);
  const plan = getPlanLabel(policy);

  let body;
  if (result?.status === BillingLoad.FAILED) {
    body = (
      <div role="alert" className={classes.errorBody}>
        <span>{line} billing didn't load.</span>
        <Button variant="text" color="secondary" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  } else if (result?.status !== BillingLoad.LOADED) {
    body = <Loading type="secondary" />;
  } else {
    const rows = getScheduleRows(result.billingDetails);
    body = rows.length ? (
      <Table
        selfContained={false}
        header={
          <>
            <TableCell>Date</TableCell>
            <TableCell>Item</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Status</TableCell>
          </>
        }
        body={rows.map((row) => (
          <TableRow key={row.key}>
            <TableCell>{formatDate(row.date)}</TableCell>
            <TableCell>{row.item}</TableCell>
            <TableCell align="right">{row.amount}</TableCell>
            <TableCell>{row.status}</TableCell>
          </TableRow>
        ))}
      />
    ) : (
      <p className={classes.muted}>No payments yet.</p>
    );
  }

  return (
    <Card
      type={result?.status === BillingLoad.FAILED ? 'error' : 'primary'}
      className={classes.card}
      component="section"
      aria-label={`${line} billing`}
    >
      <div className={classes.cardHeader}>
        <div className={classes.cardTitle}>
          <Label type="subtitle" component="h3" className={classes.heading}>
            {line}
          </Label>
          <span className={classes.policyId}>{policy.id}</span>
        </div>
        {plan && <span className={classes.muted}>{plan}</span>}
      </div>
      {actions.length > 0 && (
        <div className={classes.actions}>
          {actions.map((action) => (
            <span key={action.label} className={classes.action}>
              <Link to={paymentTabLink(accountId, policy.id)} className={classes.actionLink}>
                {action.label}
              </Link>
              {action.internal && <InternalTag />}
            </span>
          ))}
        </div>
      )}
      {body}
    </Card>
  );
};

ScheduleCard.propTypes = {
  accountId: PropTypes.string.isRequired,
  policy: PropTypes.object.isRequired,
  result: PropTypes.object,
  onRetry: PropTypes.func.isRequired,
  actions: PropTypes.array.isRequired
};

const PriceDetails = ({ policies }) => {
  const classes = useStyles();
  return (
    <Section title="Price details" type="SubSection">
      <Card type="primary" className={classes.card}>
        {policies.map((policy) => {
          const { rows, total } = getPriceRows(policy);
          return (
            <div key={policy.id} className={classes.priceBlock}>
              <Label type="subtitle" component="h3" className={classes.heading}>
                {getPolicyLine(policy)}
              </Label>
              <dl className={classes.priceRows}>
                {rows.map(({ key, label, amount }) => (
                  <React.Fragment key={key}>
                    <dt>{label}</dt>
                    <dd>{currencyFormatter(amount)}</dd>
                  </React.Fragment>
                ))}
                <dt className={classes.total}>Total</dt>
                <dd className={classes.total}>{currencyFormatter(total)}</dd>
              </dl>
            </div>
          );
        })}
      </Card>
    </Section>
  );
};

PriceDetails.propTypes = {
  policies: PropTypes.array.isRequired
};

const PaymentMethods = ({ loaded, summary }) => {
  const classes = useStyles();
  const methods = getPaymentMethods(loaded);
  const defaultFor = (method) =>
    summary.defaultMethods.find((entry) => (entry.method.id ?? null) === (method.id ?? null))?.policies ?? [];
  return (
    <Section title="Payment methods" type="SubSection">
      <Card type="primary" className={classes.card}>
        {methods.length ? (
          <ul className={classes.methods}>
            {methods.map((method) => {
              const usedBy = defaultFor(method);
              return (
                <li key={method.id ?? method.last4} className={classes.method}>
                  <div>
                    <div className={classes.methodName}>{methodLabel(method)}</div>
                    <span className={classes.muted}>
                      {[
                        method.bankName,
                        method.expMonth && method.expYear && `Expires ${method.expMonth}/${method.expYear}`
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                  {usedBy.length > 0 && <Tag>Default for {joinPolicyLines(usedBy)}</Tag>}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className={classes.muted}>{summary.complete ? 'No saved payment methods.' : 'Loading…'}</p>
        )}
        {summary.failedPolicies.length > 0 && (
          <p className={classes.muted}>Doesn't include {joinPolicyLines(summary.failedPolicies)}, which didn't load.</p>
        )}
      </Card>
    </Section>
  );
};

PaymentMethods.propTypes = {
  loaded: PropTypes.array.isRequired,
  summary: PropTypes.object.isRequired
};

const BillingHistory = ({ accountId, loaded, summary, canRefund }) => {
  const classes = useStyles();
  const rows = getBillingHistory(loaded);
  let content;
  if (rows.length) {
    content = (
      <Table
        selfContained={false}
        header={
          <>
            <TableCell>Date</TableCell>
            <TableCell>Item</TableCell>
            <TableCell>Method</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>
              <span className={classes.srOnly}>Action</span>
            </TableCell>
          </>
        }
        body={rows.map((row) => (
          <TableRow key={row.key}>
            <TableCell>{formatDate(row.date)}</TableCell>
            <TableCell>
              {row.item}
              <span className={classes.subText}>{getPolicyLine(row.policy)}</span>
            </TableCell>
            <TableCell>
              {getBillingPaymentMethod(
                row.transaction.paymentMethod,
                row.transaction.paymentMethodBrand,
                row.transaction.paymentMethodLast4
              )}
            </TableCell>
            <TableCell align="right">{currencyFormatter(row.amount)}</TableCell>
            <TableCell>{row.status}</TableCell>
            <TableCell>
              {canRefund && row.refundable && (
                <span className={classes.action}>
                  <Link
                    to={paymentTabLink(accountId, row.policy.id)}
                    aria-label={`Refund the ${formatDate(row.date)} ${getPolicyLine(row.policy)} charge`}
                    className={classes.actionLink}
                  >
                    Refund
                  </Link>
                  <InternalTag />
                </span>
              )}
            </TableCell>
          </TableRow>
        ))}
      />
    );
  } else if (summary.complete) {
    content = <p className={classes.muted}>No payments yet.</p>;
  }

  return (
    <Section title="Billing history" type="SubSection">
      <Card type="primary" className={classes.card}>
        {content}
        {summary.failedPolicies.length > 0 && (
          <p className={classes.muted}>Doesn't include {joinPolicyLines(summary.failedPolicies)}, which didn't load.</p>
        )}
        {!rows.length && !summary.complete && !summary.failedPolicies.length && <Loading type="secondary" />}
      </Card>
    </Section>
  );
};

BillingHistory.propTypes = {
  accountId: PropTypes.string.isRequired,
  loaded: PropTypes.array.isRequired,
  summary: PropTypes.object.isRequired,
  canRefund: PropTypes.bool.isRequired
};

/**
 * The condensed layout's household Billing tab: every open policy's billing on one read-only page.
 * Each policy loads on its own; one that fails gets its own error card with a retry, and the
 * summary leaves out the roll-ups until every policy has loaded. Actions open the policy's Payment
 * & Price tab.
 */
const HouseholdBilling = observer(function HouseholdBilling({ accountId }) {
  const classes = useStyles();
  const session = useSession();
  const { account } = useStore();
  const policies = getOpenPolicies(account.policies.list.toJSON());
  const [results, setResults] = useState({});

  const loadPolicy = useCallback(
    async (policyId) => {
      setResults((previous) => ({ ...previous, [policyId]: { status: BillingLoad.LOADING } }));
      try {
        const { data } = await apolloClient.query({
          query: GET_POLICY_BILLING_DETAILS,
          variables: { accountId, policyId },
          fetchPolicy: 'no-cache'
        });
        // A null result is a failure too: an empty schedule must come from a real answer.
        if (!data?.billingDetails) {
          throw new Error(`No billing details for ${policyId}`);
        }
        setResults((previous) => ({
          ...previous,
          [policyId]: { status: BillingLoad.LOADED, billingDetails: data.billingDetails }
        }));
      } catch {
        setResults((previous) => ({ ...previous, [policyId]: { status: BillingLoad.FAILED } }));
      }
    },
    [accountId]
  );

  const accountReady = account.id === accountId && !account.accountLoading;
  const policyIds = accountReady ? policies.map((policy) => policy.id).join(',') : '';
  useEffect(() => {
    if (policyIds) {
      policyIds.split(',').forEach(loadPolicy);
    }
  }, [policyIds, loadPolicy]);

  if (!accountReady) {
    return <Loading />;
  }
  if (!policies.length) {
    return <p className={classes.muted}>No billing yet.</p>;
  }

  const summary = getBillingSummary({ policies, results });

  return (
    <div className={classes.billing}>
      <BillingSummary policies={policies} summary={summary} />
      <div className={classes.columns}>
        <div className={classes.stack}>
          {policies.map((policy) => (
            <ScheduleCard
              key={policy.id}
              accountId={accountId}
              policy={policy}
              result={results[policy.id]}
              onRetry={() => loadPolicy(policy.id)}
              actions={getPolicyActions({ session, policy })}
            />
          ))}
        </div>
        <div className={classes.stack}>
          <PriceDetails policies={policies} />
          <PaymentMethods loaded={summary.loaded} summary={summary} />
        </div>
      </div>
      <BillingHistory
        accountId={accountId}
        loaded={summary.loaded}
        summary={summary}
        canRefund={Boolean(session.isInternalAgent && session.canRefundStripePayment && !session.viewOnly)}
      />
    </div>
  );
});

HouseholdBilling.propTypes = {
  accountId: PropTypes.string.isRequired
};

export default HouseholdBilling;
